// Переключатель маршрута «/» (business_apps#1486 §7): до первого ответа
// service_partners#me — Loader (иначе при включённом новом варианте мигал бы
// старый); effectiveVariant v2 → HomeV2Page, иначе текущий HomePage без
// изменений, но с тогглом у заголовка.

import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Loader } from 'sputnik-ui'

import { MainTemplate } from 'components'
import {
  trackHomeVariantView,
  setNavMetricsHomeVariant,
} from 'utils/navMetrics'
import { HOME_VARIANTS, HOME_V2_ENABLED } from '../model/constants'
import {
  selectPartnerResolved,
  selectHomeVariant,
  selectIsDefaultVariant,
  selectOwnHomeVariant,
  selectFleetSize,
  selectHomeToggleAvailable,
} from '../model/selectors'
import { variantViewedThisVisit, markVariantViewed } from '../model/storage'
import HomeVariantSwitch from './HomeVariantSwitch'

const loadHomePage = () => import('components/pages/HomePage')
const HomePage = React.lazy(loadHomePage)
const loadHomeV2Page = () => import('./HomeV2Page')
const HomeV2Page = React.lazy(loadHomeV2Page)

// Если размер парка не пришёл за это время, цель уходит без него.
const VIEW_GOAL_FALLBACK_MS = 5000
// me завис без ответа — не держим Loader дольше read-таймаута Контроль-API,
// показываем старый вариант.
const PARTNER_WAIT_MS = 8000

// control_home_variant_view — один раз за визит, с размером парка из первой
// незафильтрованной страницы списка главной.
function HomeVariantViewTracker({ variant, isDefault }) {
  const fleetSize = useSelector(selectFleetSize)
  const sent = useRef(variantViewedThisVisit())

  useEffect(() => {
    setNavMetricsHomeVariant(variant)
  }, [variant])

  useEffect(() => {
    if (sent.current) return undefined
    const send = () => {
      if (sent.current) return
      sent.current = true
      markVariantViewed()
      trackHomeVariantView({
        variant,
        isDefault,
        fleetSize: fleetSize ?? undefined,
      })
    }
    if (fleetSize !== undefined && fleetSize !== null) {
      send()
      return undefined
    }
    const timer = setTimeout(send, VIEW_GOAL_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [variant, isDefault, fleetSize])

  return null
}

export default function HomeSwitch() {
  const resolved = useSelector(selectPartnerResolved)
  const variant = useSelector(selectHomeVariant)
  const isDefault = useSelector(selectIsDefaultVariant)
  const ownVariant = useSelector(selectOwnHomeVariant)
  const toggleAvailable = useSelector(selectHomeToggleAvailable)
  const [waitedTooLong, setWaitedTooLong] = useState(false)

  // Чанки обоих вариантов грузим заранее: переключение тоггла не мигает Loader'ом.
  useEffect(() => {
    if (!HOME_V2_ENABLED) return
    loadHomePage()
    loadHomeV2Page()
  }, [])

  useEffect(() => {
    if (resolved) return undefined
    const timer = setTimeout(() => setWaitedTooLong(true), PARTNER_WAIT_MS)
    return () => clearTimeout(timer)
  }, [resolved])

  if (!HOME_V2_ENABLED) return <HomePage />

  if (!resolved && !waitedTooLong) {
    return (
      <MainTemplate>
        <Loader />
      </MainTemplate>
    )
  }

  // После фолбэка страница уже показана по старому варианту: поздний ответ me
  // её не подменяет (пропали бы поиск и позиция), вариант меняет только сам
  // пользователь тогглом.
  const effective = waitedTooLong
    ? ownVariant || HOME_VARIANTS.V1
    : variant || HOME_VARIANTS.V1
  const effectiveIsDefault = waitedTooLong ? !ownVariant : isDefault

  return (
    <>
      <HomeVariantViewTracker
        variant={effective}
        isDefault={effectiveIsDefault}
      />
      {effective === HOME_VARIANTS.V2 ? (
        <HomeV2Page />
      ) : (
        <HomePage
          titleAction={
            toggleAvailable ? (
              <HomeVariantSwitch variant={HOME_VARIANTS.V1} />
            ) : undefined
          }
        />
      )}
    </>
  )
}
